import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import io from 'socket.io-client';
import Leaderboard from './Leaderboard';
import Timer from './Timer';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import ShareButton from './ShareButton';

interface MinimalSocket {
  on(event: string, callback: (...args: any[]) => void): void;
  emit(event: string, ...args: any[]): void;
  disconnect(): void;
  id: string;
}

let socket: MinimalSocket;

interface GameState {
  status: string;
  question?: string;
  options?: string[];
  correctAnswer?: string;
  timerEnd?: number;
  leaderboard?: { name: string; score: number; pointsEarned?: number }[];
  questionNumber?: number;
}

interface Player {
  name: string;
}

interface NewQuestionData {
  question: string;
  options: string[];
  timerEnd: number;
  questionNumber: number;
}

interface RevealAnswerData {
  question: string;
  options: string[];
  correctAnswer: string;
  questionNumber: number;
  leaderboard: { name: string; score: number; pointsEarned?: number }[];
}

interface FinalQuestionData {
  question: string;
  options: string[];
  timerEnd: number;
  questionNumber: number;
}

interface GameOverData {
  leaderboard: { name: string; score: number }[];
}

const ALL_CATEGORIES: { id: string; label: string }[] = [
  { id: 'general_knowledge', label: 'General' },
  { id: 'science', label: 'Science' },
  { id: 'history', label: 'History' },
  { id: 'film_and_tv', label: 'Film & TV' },
  { id: 'music', label: 'Music' },
  { id: 'geography', label: 'Geography' },
  { id: 'sport_and_leisure', label: 'Sports' },
  { id: 'arts_and_literature', label: 'Arts & Lit' },
  { id: 'food_and_drink', label: 'Food & Drink' },
  { id: 'society_and_culture', label: 'Society' },
];
const DIFFICULTIES = ['mixed', 'easy', 'medium', 'hard'] as const;
type Difficulty = typeof DIFFICULTIES[number];

function HostingInterface() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const [players, setPlayers] = useState<Player[]>([]);
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => ALL_CATEGORIES.map((c) => c.id));
  const [difficulty, setDifficulty] = useState<Difficulty>('mixed');
  const navigate = useNavigate();

  useEffect(() => {
    const serverUrl = import.meta.env.VITE_SERVER_URL;
    socket = io(serverUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
  });
    socket.on('connect', () => {
      const hostToken = sessionStorage.getItem('hostToken');
      socket.emit('joinRoom', { roomCode, hostToken });
    });
    socket.on('playerList', (playerNames: string[]) => {
      setPlayers(playerNames.map((name) => ({ name })));
    });
    socket.on('error', (msg: string) => {
      Sentry.captureMessage(msg);
      alert(msg);
    });
    socket.on('gameStarting', () =>
      setGameState({ status: 'starting', timerEnd: Date.now() + 5000 })
    );
    socket.on('newQuestion', (data: NewQuestionData) =>
      setGameState({ status: 'question', ...data })
    );
    socket.on('revealAnswer', (data: RevealAnswerData) =>
      setGameState({ status: 'reveal', ...data })
    );
    socket.on('finalQuestionWager', () => setGameState({ status: 'wager' }));
    socket.on('finalQuestion', (data: FinalQuestionData) =>
      setGameState({ status: 'finalQuestion', ...data })
    );
    socket.on('gameOver', (data: GameOverData) =>
      setGameState({ status: 'over', leaderboard: data.leaderboard })
    );
  
    return () => {
      socket.disconnect();
    };
  }, [roomCode]);

  const toggleCategory = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const toggleAllCategories = () => {
    setSelectedCategories((prev) =>
      prev.length > 0 ? [] : ALL_CATEGORIES.map((c) => c.id)
    );
  };

  const startGame = () => {
    if (selectedCategories.length === 0) {
      alert('Pick at least one category');
      return;
    }
    if (window.confirm('Is everyone ready to start the game?')) {
      try {
        socket.emit('startGame', { categories: selectedCategories, difficulty });
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to start game');
      }
    }
  };

  const startNewGame = () => {
    if (window.confirm('Start a new game with the same players and room code?')) {
      try {
        socket.emit('startNewGame');
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to start new game');
      }
    }
  };

  const forceAdvance = () => {
    if (window.confirm('Are you sure you want to skip to the next question?')) {
      try {
        socket.emit('forceAdvance');
      } catch (err) {
        Sentry.captureException(err);
        alert('Failed to skip');
      }
    }
  };

  return (
    <div className="flex flex-col items-center w-full max-w-4xl mx-auto">
      <h1 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
        Host - Room: {roomCode}
      </h1>
      {!gameState && (
        <>
          <div className="w-full max-w-lg mb-6">
            <div className="flex justify-end mb-2">
              <button
                type="button"
                onClick={toggleAllCategories}
                className="text-sm text-gray-300 hover:text-white underline"
              >
                {selectedCategories.length > 0 ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {ALL_CATEGORIES.map((cat) => {
                const selected = selectedCategories.includes(cat.id);
                return (
                  <button
                    key={cat.id}
                    onClick={() => toggleCategory(cat.id)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition border ${
                      selected
                        ? 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600'
                        : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300 border-gray-600'
                    }`}
                  >
                    {selected ? '✓ ' : ''}{cat.label}
                  </button>
                );
              })}
            </div>
            <h3 className="text-lg font-semibold mb-2 text-center">Difficulty</h3>
            <div className="flex justify-center gap-2 flex-wrap">
              {DIFFICULTIES.map((d) => (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition ${
                    difficulty === d
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-gray-700 bg-opacity-50 hover:bg-opacity-70 text-gray-300'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <button
              onClick={startGame}
              disabled={selectedCategories.length === 0}
              className={`text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg ${
                selectedCategories.length === 0
                  ? 'bg-blue-600 opacity-50 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              Start Game
            </button>
            {roomCode && <ShareButton roomCode={roomCode} />}
          </div>
          <h2 className="text-2xl font-semibold mb-4">Players ({players.length})</h2>
          <ul className="mb-6 text-lg space-y-2">
            {players.map((p) => (
              <li key={p.name} className="bg-gray-800 bg-opacity-50 p-2 rounded-lg">
                {p.name}
              </li>
            ))}
          </ul>
        </>
      )}
      {gameState?.status === 'starting' && (
        <div className="text-xl font-medium">
          Game starting in <Timer endTime={gameState.timerEnd!} />
        </div>
      )}
      {gameState?.status === 'question' && (
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">Question: {gameState.questionNumber} / 7</p>
          <p className="text-xl md:text-2xl mb-4">{gameState.question}</p>
          <div className="mb-4">
            <Timer endTime={gameState.timerEnd!} />
          </div>
          <div className="w-full space-y-3">
            {gameState.options?.map((opt, index) => (
              <p
                key={opt}
                className="w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium bg-gray-800 bg-opacity-50 text-white"
              >
                {String.fromCharCode(65 + index)}. {opt}
              </p>
            ))}
          </div>
          <button
            onClick={forceAdvance}
            className="mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition duration-300 text-lg"
          >
            Skip to Next Question
          </button>
        </div>
      )}
      {gameState?.status === 'reveal' && (
        <div className="text-center w-full">
          <p className="text-lg font-semibold mb-2">
            {gameState.questionNumber === 8 ? 'Final Question' : `Question: ${gameState.questionNumber} / 7`}
          </p>
          <p className="text-xl md:text-2xl mb-4">{gameState.question}</p>
          <div className="w-full space-y-3 mt-4">
            {gameState.options!.map((opt, index) => (
              <p
                key={opt}
                className={`w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium ${
                  opt === gameState.correctAnswer ? 'bg-green-600 text-white' : 'bg-gray-800 bg-opacity-50 text-white'
                }`}
              >
                {String.fromCharCode(65 + index)}. {opt}
              </p>
            ))}
          </div>
          {gameState.leaderboard && gameState.leaderboard.length > 0 && (
            <div className="mt-6 w-full max-w-md mx-auto">
              <h4 className="text-sm font-semibold mb-2 text-gray-300 uppercase tracking-wide">Standings</h4>
              <div className="space-y-1">
                {gameState.leaderboard.slice(0, 3).map((p, i) => {
                  const earned = p.pointsEarned ?? 0;
                  return (
                    <div
                      key={`${p.name}-${i}`}
                      className="flex justify-between px-3 py-2 rounded-lg text-base font-medium bg-gray-800 bg-opacity-60"
                    >
                      <span>{i + 1}. {p.name}</span>
                      <span>
                        {p.score}{' '}
                        <span className={earned > 0 ? 'text-green-400' : 'text-red-400'}>
                          (+{earned})
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {gameState?.status === 'wager' && (
        <div className="text-center">
          <p className="text-xl mb-4">Players are submitting their wagers...</p>
          <button onClick={forceAdvance} className="mt-4 bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition duration-300 text-lg">
            Skip to Final Question
          </button>
        </div>
      )}
      {gameState?.status === 'finalQuestion' && (
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">Final Question</p>
          <p className="text-xl md:text-2xl mb-4">{gameState.question}</p>
          <div className="mb-4">
            <Timer endTime={gameState.timerEnd!} />
          </div>
          <div className="w-full space-y-3">
            {gameState.options?.map((opt, index) => (
              <p
                key={opt}
                className="w-full p-2 sm:p-4 rounded-lg text-base sm:text-lg font-medium bg-gray-800 bg-opacity-50 text-white"
              >
                {String.fromCharCode(65 + index)}. {opt}
              </p>
            ))}
          </div>
        </div>
      )}
      {gameState?.status === 'over' && (
        <div className="text-center">
          <Leaderboard players={gameState.leaderboard!} />
          <button
            onClick={startNewGame}
            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
          >
            Start New Game
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 text-lg"
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
}

export default HostingInterface;
